import { Component, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
    CardTypeMasks,
    CustomerFacade,
    MediumFacade,
    MediumGroup,
    MediumGroupType,
    MediumType,
    RelatedCardFacade,
    SessionService,
    TravelCardFullType,
    TravelCardType
} from '@ridango/components';
import { AppStartupService, BaseComponent, CardStatus, RegionProperty, RelatedCard } from '@ridango/core';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TravelCardInput } from './purchase-medium.model';
import Env from '@ridango/core/env';

@Component({
    selector: 'rwidget-purchase-medium',
    templateUrl: './purchase-medium.component.html'
})
export class PurchaseMediumComponent extends BaseComponent implements OnInit, OnDestroy {
    private unregisteredCardDialog;
    private someoneElseCardDialog;

    @ViewChild('unregisteredCardModal', { static: true }) unregisteredCardModalRef: TemplateRef<any>;
    @ViewChild('someoneElseCardModal', { static: true }) someoneElseCardModalRef: TemplateRef<any>;

    public mediumGroupTypes = MediumGroupType;

    @Input() locale;
    @Input() step = null;
    @Input() isEmpty = false;
    @Input() allowPurchaseOnlyAddedCards: boolean;

    private resetCardInput = new Subject<void>();
    private disableCardInput = new Subject<boolean>();

    resetCardInput$ = this.resetCardInput.asObservable();
    disableCardInput$ = this.disableCardInput.asObservable();
    allCards$ = this.relatedCardFacade.typeTravelcardCards$;
    selectedCard$ = this.relatedCardFacade.selectedCard$;

    selectedMedium$ = this.mediumFacade.medium$;

    cardTypeMask$: Observable<number> = this.mediumFacade.cardTypeMask$;
    mediumGroups$: Observable<MediumGroup[]> = this.mediumFacade.availableMediumGroups$;
    selectedMediumGroup$: Observable<MediumGroup> = this.mediumFacade.selectedMediumGroup$;

    cardStatus$ = this.relatedCardFacade.cardStatus$;
    isCardRegistered: boolean;
    isUserCard: boolean;
    allAllowedCards: RelatedCard[] = [];

    dropdownVisible = false;
    dropdownDisabled = false;

    constructor(
        @Inject('ENV') public environment: Env,
        private relatedCardFacade: RelatedCardFacade,
        private session: SessionService,
        private mediumFacade: MediumFacade,
        private appConfig: AppStartupService,
        private customerFacade: CustomerFacade,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit() {
        if (this.session.getLoginStatus()) {
            this.relatedCardFacade.fetchAllRelatedCards();
            this.customerFacade.getCustomerProfile();
        }

        this.allCards$.pipe(takeUntil(this.componentDestroyed$)).subscribe((cards) => {
            if (cards.length) {
                this.setAllowedCards(cards);
            }
        });

        this.cardTypeMask$.pipe(takeUntil(this.componentDestroyed$)).subscribe((ctm) => {
            // Calculate available medium type groups here
            const groups = this.determineMediumGroupsByMask(ctm);
            this.mediumFacade.setAvailableMediumGroups(groups);
            if (groups.length === 1) {
                this.mediumFacade.setSelectedMediumGroup(groups[0]);
            }
        });

        this.selectedMediumGroup$.pipe(takeUntil(this.componentDestroyed$)).subscribe((group) => {
            if (group) {
                this.resetCardInputForm();
            }
        });

        this.selectedCard$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((relatedCard) => !!relatedCard && this.relatedCardSelected(relatedCard));

        // disable other inputs if voucher medium is selected
        this.mediumFacade.medium$.pipe(takeUntil(this.componentDestroyed$)).subscribe((medium) => {
            if (medium.cardType === TravelCardType.Voucher) {
                this.resetCardInputForm();
                this.dropdownDisabled = true;
                this.disableCardInput.next(true);
            } else {
                this.dropdownDisabled = false;
                this.disableCardInput.next(false);
            }
        });

        this.cardStatus$.pipe(takeUntil(this.componentDestroyed$)).subscribe((cardStatus: CardStatus) => {
            if (cardStatus) {
                this.isCardRegistered = cardStatus.registered;
                this.isUserCard = cardStatus.registered_to_current_customer;

                if (!this.isCardRegistered && this.isUserCard === null) {
                    this.unregisteredCardDialog = this.dialog.open(this.unregisteredCardModalRef, {
                        width: '620px',
                        disableClose: true
                    });
                }

                if (this.isCardRegistered && this.isUserCard !== null && !this.isUserCard) {
                    this.someoneElseCardDialog = this.dialog.open(this.someoneElseCardModalRef, {
                        width: '620px',
                        disableClose: true
                    });
                }
            }
        });
    }

    ngOnDestroy() {
        this.relatedCardFacade.resetRelatedCardStatus();
        super.ngOnDestroy();
    }

    private resetCardInputForm() {
        this.mediumFacade.resetSelectedMedium();
        this.resetCardInput.next();
    }

    private relatedCardSelected(card: RelatedCard) {
        this.setMedium({
            type: MediumType.RelatedCard,
            value: card,
            isValid: true
        });
        this.dropdownVisible = false;
        this.resetCardInput.next();
    }

    setMedium(travelCardInput: TravelCardInput) {
        this.mediumFacade.resetSelectedMedium();
        const { type, value, isValid } = travelCardInput;

        if (
            type === TravelCardType.TravelCardU ||
            type === TravelCardType.TravelCardN ||
            type === TravelCardType.TravelCardForeign ||
            type === TravelCardType.TravelCardUTN2 ||
            type === TravelCardType.EstId ||
            type === TravelCardType.TravelCardHSL ||
            type === TravelCardType.TravelCardUTN2
        ) {
            if (this.environment.RELATED_CARD?.CHECK_STATUS) {
                this.relatedCardFacade.fetchCardStatus(value, TravelCardFullType.UTN);
            }
            // NB! Must check handling of N and F handling

            // this.cardNumber = value;
            this.relatedCardFacade.setSelectedRelatedCard(null);
            /* saveMediumState( null ); */

            if (isValid) {
                const cardNumPrefix: RegionProperty = this.appConfig.getPropertyByCode('CARD_NUMBER_PREFIX');
                const cardPrefix = cardNumPrefix ? cardNumPrefix.value : '';

                this.mediumFacade.setTravelCardMedium(
                    cardPrefix + value,
                    this.setMediumType(type as TravelCardType),
                    type as TravelCardType
                );
            }
        }

        if (type === MediumType.Email) {
            this.relatedCardFacade.setSelectedRelatedCard(null);
            /* saveMediumState( null ); */

            if (isValid) {
                this.mediumFacade.setEmailMedium(value);
            }
        }

        if (type === MediumType.RelatedCard) {
            if (isValid) {
                this.mediumFacade.setRelatedCardMedium(value);
            }
        }
    }

    setMediumType(type: TravelCardType) {
        switch (type) {
            case TravelCardType.TravelCardU:
                return MediumType.TravelCardU;
            case TravelCardType.TravelCardN:
                return MediumType.TravelCardN;
            case TravelCardType.TravelCardForeign:
                return MediumType.TravelCardF;
            case TravelCardType.TravelCardUTN2:
                return MediumType.TravelCardT;
            case TravelCardType.EstId:
                return MediumType.EstId;
            case TravelCardType.TravelCardHSL:
                return MediumType.TravelCardH;
        }
    }

    modalAction(resetInput = false) {
        if (resetInput) {
            this.resetCardInputForm();
        }
        this.dialog.closeAll();
    }

    setRelatedCard(card: RelatedCard) {
        this.relatedCardFacade.setSelectedRelatedCard(card);
    }

    dropdownToggle(value: boolean) {
        this.dropdownVisible = value;
    }
    /* TODO: Voucher related handling still needs to do,
    but as was not in scope of Gotland will be left
    for later time development  */
    voucherSelected(voucher) {
        /* this.ticketsService.setMedium(this.ticketUid, null);
        this.ticketsService.setRelatedCard(this.ticketUid, null);
        this.ticketsService.setMedium(this.ticketUid, voucher);
        this.ticketsService.setSelectedVoucher(voucher); */
    }

    voucherCancelled() {
        /* if (
            this.ngRedux.getState().tickets.tickets[this.ticketUid].medium_object &&
            this.ngRedux.getState().tickets.tickets[this.ticketUid].medium_object.type === 'S'
        ) {
            // if voucher email already entered
            this.mediumService.setSelectedMedium(null);
            this.ticketsService.setMedium(this.ticketUid, null);
            this.showTicket = false;
        } */
    }

    voucherNotValid() {
        /* this.showTicket = false; */
    }

    private determineMediumGroupsByMask(mask): MediumGroup[] {
        const groups = [];

        if (!mask) {
            return groups;
        }

        const masks = Object.keys(CardTypeMasks);

        masks.forEach((key) => {
            const cardType = CardTypeMasks[key];
            if (cardType && mask & cardType.mask) {
                groups.push(new MediumGroup(cardType.medium, cardType.name, cardType.allowedCardTypes));
            }
        });

        return groups;
    }

    setAllowedCards(cards) {
        this.mediumGroups$.pipe(takeUntil(this.componentDestroyed$)).subscribe((groups) => {
            this.allAllowedCards = [];

            const mediumCardTypes = groups.find(({ type }) => type === 'Travelcard');
            if (mediumCardTypes) {
                cards.forEach((card) => {
                    if (mediumCardTypes.travelcardTypes.includes(card.card_type)) {
                        this.allAllowedCards.push(card);
                    }
                });
            }
        });
    }
}
